import { createReducer } from '@reduxjs/toolkit';
import { Nullable } from 'modules/types/common';
import { LoginType } from 'store/authentication/types';
import * as actions from 'store/global/actions';
import {
  defaultInitialState,
  DefaultState,
  setFetchingFailState,
  setFetchingSuccessState
} from 'store/setLocalStore';

export interface UserCoords {
  latitude: number;
  longitude: number;
}

export type TermsAndConditionsTypeValue =
  | 'general'
  | 'provider'
  | 'costEstimator'
  | 'changeHealthcare'
  | 'triage'
  | 'eVisitPrivacyNotice'
  | 'eVisitConsentToTreat'
  | 'eVisitTermsOfUse';

export interface TermsAndConditionsResponse {
  version: string;
  content: string;
  type: TermsAndConditionsTypeValue;
}

export enum TermsAndConditionsType {
  GENERAL = 'general',
  PROVIDER_SEARCH = 'provider',
  COST_ESTIMATOR = 'costEstimator',
  CHANGE_HEALTH = 'changeHealthcare',
  TRIAGE_DISCLAIMER = 'triage',
  E_VISIT_PRIVACY_NOTICE = 'eVisitPrivacyNotice',
  E_VISIT_CONSENT_TO_TREAT = 'eVisitConsentToTreat',
  E_VISIT_TERMS_OF_USE = 'eVisitTermsOfUse'
}

export type TermsAndConditions = Record<TermsAndConditionsType, TermsAndConditionsResponse>;

export interface TemporaryTermsAndConditions {
  COST_ESTIMATOR: boolean;
  TRIAGE_DISCLAIMER: boolean;
  PROVIDER_SEARCH: boolean;
  CONNECT_CARE: boolean;
  E_VISIT_PRIVACY_NOTICE: boolean;
  E_VISIT_CONSENT_TO_TREAT: boolean;
  E_VISIT_TERMS_OF_USE: boolean;
}

export interface GlobalState {
  termsAndConditions: undefined | TermsAndConditions;
  questions: any;
  isFetching: boolean;
  error: Nullable<Error>;
  temporaryTCAccept: TemporaryTermsAndConditions;
  initialNavigationComplete: boolean;
  navigationPathAfterAuthentication: string;
  navigationParamAfterAuthentication: string;
  hasFetchedDependencies: boolean;
  isFetchingDependencies: boolean;
  mhpStatus: DefaultState;
  loginType: LoginType;
}

export const defaultState: GlobalState = {
  termsAndConditions: undefined,
  questions: null,
  isFetching: false,
  error: null,
  temporaryTCAccept: {
    COST_ESTIMATOR: false,
    TRIAGE_DISCLAIMER: false,
    PROVIDER_SEARCH: false,
    CONNECT_CARE: false,
    E_VISIT_PRIVACY_NOTICE: false,
    E_VISIT_CONSENT_TO_TREAT: false,
    E_VISIT_TERMS_OF_USE: false
  },
  initialNavigationComplete: false,
  mhpStatus: defaultInitialState(),
  navigationPathAfterAuthentication: '',
  navigationParamAfterAuthentication: '',
  hasFetchedDependencies: false,
  isFetchingDependencies: false,
  loginType: LoginType.PASSWORD
};

const globalReducer = createReducer(defaultState, {
  [actions.GET_TERMS_AND_CONDITIONS]: (state, action) => {
    const { termsType } = action?.payload || {};
    state.isFetching = !(
      termsType &&
      state.termsAndConditions &&
      state.termsAndConditions[termsType as TermsAndConditionsType]
    ); // Don't set isFetching if we have the data
    state.error = null;
  },

  [actions.GET_TERMS_AND_CONDITIONS_SUCCESS]: (state, action) => {
    const { data } = action.payload || {};
    if (data.type) {
      // Partial Payload response
      if (!state.termsAndConditions) {
        state.termsAndConditions = {} as TermsAndConditions;
      }
      state.termsAndConditions[data.type.toLowerCase() as TermsAndConditionsType] = data;
    } else {
      state.termsAndConditions = data;
    }
    state.isFetching = false;
  },

  [actions.GET_TERMS_AND_CONDITIONS_FAIL]: (state, action) => {
    state.isFetching = false;
    state.error = action.error;
  },
  [actions.SET_TEMPORARY_TC]: (state, action) => {
    state.temporaryTCAccept[action.payload as keyof TemporaryTermsAndConditions] = true;
  },

  [actions.INITIAL_NAVIGATION_COMPLETE]: state => {
    state.initialNavigationComplete = true;
  },

  [actions.SET_NAVIGATION_AFTER_AUTHENTICATION]: (state, action) => {
    state.navigationPathAfterAuthentication = action.payload;
  },

  [actions.SET_NAVIGATION_PARAM_AFTER_AUTHENTICATION]: (state, action) => {
    state.navigationParamAfterAuthentication = action.payload;
  },

  [actions.GET_DEPS]: state => {
    state.isFetchingDependencies = true;
    state.hasFetchedDependencies = false;
    state.error = null;
  },
  [actions.GET_DEPS_SUCCESS]: state => {
    state.hasFetchedDependencies = true;
    state.error = null;
    state.isFetchingDependencies = false;
  },
  [actions.GET_DEPS_ERROR]: (state, payload) => {
    state.error = payload.error;
    state.isFetchingDependencies = false;
    state.hasFetchedDependencies = true;
  },

  [actions.MY_HEALTH_STATUS]: (state, action) => {
    state.mhpStatus.isFetching = true;
    state.mhpStatus.error = null;
    state.mhpStatus.data = undefined;
  },
  [actions.MY_HEALTH_STATUS_SUCCESS]: (state, action) => {
    setFetchingSuccessState(state.mhpStatus, action);
  },
  [actions.MY_HEALTH_STATUS_FAIL]: (state, action) => setFetchingFailState(state.mhpStatus, action),
  [actions.SET_LOGIN_TYPE]: (state, action) => {
    state.loginType = action.payload;
  }
});

export default globalReducer;
